export const ProjectSettingName = {
  documentRepositoryEnabled: "ENABLE_DOCUMENT_REPOSITORY_DATA",
  imdrfDisabled: "DISABLE_IMDRF",
  includeRadiology: "INCLUDE_RADIOLOGY",
  medicationsEnabled: "ENABLE_MEDICATIONS",
  timelineEnabled: "ENABLE_TIMELINE",
  codeOffsetEnabled: "ENABLE_CODE_OFFSET",
  pathologyReportsEnabled: "ENABLE_PATHOLOGY_REPORTS",
} as const;

export type ProjectSettingNameType =
  (typeof ProjectSettingName)[keyof typeof ProjectSettingName];

export enum RadiologyOptionValue {
  none = "FALSE",
  reports = "REPORTS",
  images = "IMAGES",
}
type ProjectSettingDefaultType = {
  [ProjectSettingName.codeOffsetEnabled]: boolean;
  [ProjectSettingName.medicationsEnabled]: boolean;
  [ProjectSettingName.documentRepositoryEnabled]: boolean;
  [ProjectSettingName.timelineEnabled]: boolean;
  [ProjectSettingName.includeRadiology]: RadiologyOptionValue;
  [ProjectSettingName.pathologyReportsEnabled]: boolean;
};

export const ProjectSettingDefaultValue: ProjectSettingDefaultType = {
  [ProjectSettingName.codeOffsetEnabled]: false,
  [ProjectSettingName.medicationsEnabled]: false,
  [ProjectSettingName.documentRepositoryEnabled]: false,
  [ProjectSettingName.timelineEnabled]: false,
  [ProjectSettingName.includeRadiology]: RadiologyOptionValue.none,
  [ProjectSettingName.pathologyReportsEnabled]: false,
};

function getBooleanSetting(
  settings: ProjectSetting[],
  settingName: string,
): boolean {
  const setting = settings.find((s) => s.name === settingName);
  if (!setting?.value) return false;
  return /^true$/i.test(setting.value);
}

export function getCodeOffsetEnabled(
  projectSettings?: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  return getBooleanSetting(
    projectSettings,
    ProjectSettingName.codeOffsetEnabled,
  );
}

export function getMedicationsEnabled(
  projectSettings?: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  return getBooleanSetting(
    projectSettings,
    ProjectSettingName.medicationsEnabled,
  );
}

export function getDocumentRepositoryEnabled(
  projectSettings?: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  return getBooleanSetting(
    projectSettings,
    ProjectSettingName.documentRepositoryEnabled,
  );
}

export function getProcedureTimelineEnabled(
  projectSettings?: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  return getBooleanSetting(projectSettings, ProjectSettingName.timelineEnabled);
}

export function getIncludeRadiology(
  projectSettings: ProjectSetting[],
): RadiologyOptionValue {
  if (!projectSettings) return RadiologyOptionValue.none;
  if (getRadiologyImagesEnabled(projectSettings))
    return RadiologyOptionValue.images;
  if (getRadiologyReportsEnabled(projectSettings))
    return RadiologyOptionValue.reports;
  return RadiologyOptionValue.none;
}

export function getRadiologyImagesEnabled(
  projectSettings?: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  const setting = projectSettings.find(
    (s) => s.name === ProjectSettingName.includeRadiology,
  );
  if (setting === undefined) return false;
  const matchRegex = new RegExp(RadiologyOptionValue.images, "i");
  return !!setting.value.match(matchRegex);
}

function getRadiologyReportsEnabled(
  projectSettings: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;

  // Radiology Images requires reports enabled
  if (getRadiologyImagesEnabled(projectSettings)) return true;

  const setting = projectSettings.find(
    (s) => s.name === ProjectSettingName.includeRadiology,
  );
  if (setting === undefined) return false;
  const matchRegex = new RegExp(RadiologyOptionValue.reports, "i");
  return !!setting.value.match(matchRegex);
}

export function getImdrfEnabled(projectSettings?: ProjectSetting[]): boolean {
  //Default to true and return the inverse below
  if (!projectSettings) return true;
  return !getBooleanSetting(projectSettings, ProjectSettingName.imdrfDisabled);
}

export function getPathologyReportsEnabled(
  projectSettings: ProjectSetting[],
): boolean {
  if (!projectSettings) return false;
  return getBooleanSetting(
    projectSettings,
    ProjectSettingName.pathologyReportsEnabled,
  );
}
